import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import { useCallback, useContext, useState } from "react";
import {
  ApprovalParams,
  PauseReason,
  Role,
  Task,
} from "../../../app/backendTypes";
import { HttpClientContext, X_REQUEST_ID } from "../../../app/client";
import { useApprover, useAppSelector } from "../../../app/hooks";
import Morselbar from "../Morselbar";

function ApprovalActions(props: { task: Task; callback: () => void }) {
  const { task, callback } = props;

  let index: number | undefined;

  if (task.approvals !== undefined && task.approvals !== null) {
    for (let i = 0; i < task.approvals.length; i++) {
      const approval = task.approvals[i];

      if (
        approval.status.kind === "Idle" ||
        approval.status.kind === "Notified"
      ) {
        index = i;
        break;
      }
    }
  }

  const [loading, setLoading] = useState(false);

  const { approver } = useApprover(task.id, index, false);

  const auth = useAppSelector((state) => state.auth);

  if (task.status.kind !== "Paused") {
    return <></>;
  } else if (task.status.inner.reason !== PauseReason.WaitingForApproval) {
    return <></>;
  }

  if (!auth.roles.includes(Role.Administrator)) {
    if (approver === null) return <></>;
    if (approver.employee.id !== auth.userId) return <></>;
  }

  return (
    <>
      <ApproveButton
        taskId={task.id}
        loading={loading}
        setLoading={setLoading}
        callback={callback}
      />
      <DenyButton
        taskId={task.id}
        loading={loading}
        setLoading={setLoading}
        callback={callback}
      />
    </>
  );
}

export default ApprovalActions;

function ApproveButton(props: {
  taskId: string;
  loading: boolean;
  setLoading: (val: boolean) => void;
  callback: () => void;
}) {
  const { taskId, loading, setLoading, callback } = props;

  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [approveNote, setApproveNote] = useState<string>("");

  const client = useContext(HttpClientContext);

  const approveTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    const note = approveNote || undefined;
    const payload: ApprovalParams = { kind: "Approved", inner: { note } };

    client
      ?.post(`/api/tasks/${taskId}/approval`, payload)
      .then(() => {
        callback();
        setDialogOpen(false);
      })
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading, approveNote, client, taskId, callback]);

  return (
    <>
      <Button
        color="success"
        variant="outlined"
        disabled={loading}
        onClick={() => setDialogOpen(true)}
      >
        Approve
      </Button>

      <Dialog open={dialogOpen}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography>
              This action will{" "}
              <Typography
                component="span"
                color="success.main"
                fontWeight="bold"
              >
                approve
              </Typography>{" "}
              the task or request
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <br />
          <TextField
            onChange={(e) => setApproveNote(e.target.value.trim())}
            placeholder="(Optional) Add a note to approval"
            label="Note"
            size="small"
            fullWidth
          />
          <br />
          <br />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => approveTask()}
              disabled={loading}
              color="success"
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to approve task"
        error={error}
      />
    </>
  );
}

function DenyButton(props: {
  taskId: string;
  loading: boolean;
  setLoading: (val: boolean) => void;
  callback: () => void;
}) {
  const { taskId, loading, setLoading, callback } = props;

  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [denyReason, setDenyReason] = useState<string>("");

  const client = useContext(HttpClientContext);

  const denyTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    const reason = denyReason || undefined;
    const payload: ApprovalParams = { kind: "Denied", inner: { reason } };

    client
      ?.post(`/api/tasks/${taskId}/approval`, payload)
      .then(() => {
        callback();
        setDialogOpen(false);
      })
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [setLoading, denyReason, client, taskId, callback]);

  return (
    <>
      <Button
        color="error"
        variant="outlined"
        disabled={loading}
        onClick={() => setDialogOpen(true)}
      >
        Deny
      </Button>

      <Dialog open={dialogOpen}>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography>
              This action will{" "}
              <Typography component="span" color="error" fontWeight="bold">
                deny
              </Typography>{" "}
              the task or request
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <br />
          <TextField
            onChange={(e) => setDenyReason(e.target.value.trim())}
            placeholder="(Optional) Specify a reason for denial"
            label="Reason"
            fullWidth
            size="small"
          />
          <br />
          <br />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => denyTask()}
              disabled={loading}
              color="error"
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to deny task"
        error={error}
      />
    </>
  );
}
